import { useRoute, useRouter } from 'vue-router'
import type {
  LocationQuery,
  LocationQueryRaw,
  RouteParamsRawGeneric
} from 'vue-router'

/**
 * The filters the list keeps in the URL. `useFilters` is the only thing that writes
 * query state a list owns, and it scopes every key it writes under this prefix — the
 * rest of a route's query belongs to that route (a preset, a tab, `?back=`).
 */
const FILTER_QUERY_PREFIX = 'filters['

function listQuery(query: LocationQuery): LocationQueryRaw {
  return Object.fromEntries(
    Object.entries(query).filter(([key]) => key.startsWith(FILTER_QUERY_PREFIX))
  )
}

/**
 * How a modal-by-route opens and closes.
 *
 * Opening one is a `router.push`, so back closes it and the URL can be shared; closing
 * is a `replace`, so the modal does not stay in history for the back button to reopen.
 *
 * Both carry the list's filters, which is the whole reason to use them: a `router.push`
 * that names no query gets an empty one, the router does not inherit it, so opening a modal
 * by hand drops `filters[…]` — the table behind the modal reloads unfiltered and closing
 * lands on a bare list.
 *
 * Only this page's own modal routes go through here. A push to a *different* screen stays a
 * plain `router.push`: another page's filters are not this page's to carry.
 */
export function useModalRoutes(listRouteName: string) {
  const route = useRoute()
  const router = useRouter()

  function openModal(to: {
    name: string
    params?: RouteParamsRawGeneric
    query?: LocationQueryRaw
  }) {
    router.push({
      name: to.name,
      params: to.params,
      query: { ...listQuery(route.query), ...to.query }
    })
  }

  function closeModal() {
    router.replace({ name: listRouteName, query: listQuery(route.query) })
  }

  /**
   * A child modal closes into its parent rather than to the list, and the parent's ids
   * are already in the route.
   */
  function closeTo(
    name: string,
    params?: RouteParamsRawGeneric,
    query?: LocationQueryRaw
  ) {
    router.replace({
      name,
      params,
      query: { ...listQuery(route.query), ...query }
    })
  }

  return { openModal, closeModal, closeTo }
}
